/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import isNil from 'lodash/isNil';

import {
    CHANGE_PASSES_BY_GUEST_TYPE,
    CHANGE_PASSES_DETAILS,
    CHANGE_TICKET_HOLDER_DETAILS,
    CLOSE_MODIFY_MODAL,
    CLOSE_PASS_MODAL,
    GET_DRAFT_PRICE_SUMMARY_ERROR,
    GET_DRAFT_PRICE_SUMMARY_REQUEST,
    GET_DRAFT_PRICE_SUMMARY_SUCCESS,
    GET_MY_PASSES_ERROR,
    GET_MY_PASSES_REQUEST,
    GET_MY_PASSES_SUCCESS,
    GET_MY_RESERVATIONS_ERROR,
    GET_MY_RESERVATIONS_REQUEST,
    GET_MY_RESERVATIONS_SUCCESS,
    GET_ORDER_DETAILS_ERROR,
    GET_ORDER_DETAILS_REQUEST,
    GET_ORDER_DETAILS_SUCCESS,
    GET_QR_CODES_ERROR,
    GET_QR_CODES_SUCCESS,
    GET_UPDATED_ORDER_SUMMARY_ERROR,
    GET_UPDATED_ORDER_SUMMARY_REQUEST,
    GET_UPDATED_ORDER_SUMMARY_SUCCESS,
    OPEN_MODIFY_MODAL,
    OPEN_PASS_MODAL,
    REMOVE_RESERVATION_FROM_CART_SUCCESS,
    RESET_SITE_PASS,
    SAVE_PENDING_MODIFICATIONS_SUCCESS,
    TOGGLE_NEED_TO_KNOW_CHECKBOX,
    TOGGLE_SAVE_TO_ACCOUNT,
    TOGGLE_SITE_PASS_INFO_CHECKBOX,
    UPDATE_SITE_PASS_REMOVED_RESERVATIONS_IDS,
    UPDATE_SITE_PASS_RESERVATIONS,
    UPDATE_SITE_PASS_TOTAL_PRICE,
} from '../actions/orderDetailsActions';

export const orderDetailsInitialState = {
    isLoading: true,
    isModifyModalOpen: false,
    isPassModalOpen: false,
    isNeedToKnowChecked: false,
    isSitePassInfoChecked: false,
    hasBeenModified: false,
    reservation: {
        facility_id: '',
        tour_id: '',
        tour_date: '',
        tour_time: '',
        passes: [],
    },
    price_summary: {
        summary_items: [],
        previous_payments: 0,
        balance_owed: 0,
        loading: false,
    },
    validation_results: {
        required_pass_count_by_guest_type_id: {},
    },
    reservation_cost_analysis: {
        cost_savings_available: false,
        description: '',
    },
    facility_guest_types: [],
    tour_notes: [],
    tour_name: '',
    school_details_validations: {
        summary: [],
        displayValidationError: false,
    },
    ticketHolderDetails: {
        firstName: '',
        lastName: '',
        email: '',
        country: '',
        state: '',
        zipCode: '',
        vehicleType: '',
        licensePlate: '',
        totalNumberOfPeople: 0,
    },
    passes: [],
    passes_by_guest_type_id: {},
    validationRefs: [],
    shouldSaveToAccount: false,
    sitePass: {
        reservations: [],
        removedReservationIds: [],
        totalPrice: 0,
    },
    qrCodes: {},
};

export const orderDetailsReducers = (state = orderDetailsInitialState, action) => {
    switch (action.type) {
        case TOGGLE_SAVE_TO_ACCOUNT:
            return {
                ...state,
                shouldSaveToAccount: action.payload,
            };
        case CHANGE_TICKET_HOLDER_DETAILS: {
            const newValues = {
                ...action.payload,
            };
            if (newValues?.vehicleType === 'rental') {
                newValues.licensePlate = '';
            }

            return {
                ...state,
                ticketHolderDetails: {
                    ...state.ticketHolderDetails,
                    ...newValues?.ticketHolderDetails,
                },
                hasBeenModified: newValues?.hasBeenModified,
            };
        }
        case CHANGE_PASSES_DETAILS: {
            return {
                ...state,
                passes: action.payload,
            };
        }
        case CHANGE_PASSES_BY_GUEST_TYPE: {
            const newValues = {
                ...action.payload,
            };
            return {
                ...state,
                passes_by_guest_type_id: newValues?.passes,
                hasBeenModified: newValues?.hasBeenModified,
            };
        }
        case SAVE_PENDING_MODIFICATIONS_SUCCESS:
            return { ...state, reservation: action.payload };
        case GET_ORDER_DETAILS_REQUEST:
            return { ...state, isLoading: true };
        case GET_ORDER_DETAILS_SUCCESS:
            return {
                ...state,
                ...action.payload,
                isLoading: false,
                hasBeenModified: false,
                isNeedToKnowChecked:
                    action?.payload?.reservation?.need_to_know_checked || false,
                isSitePassInfoChecked:
                    action?.payload?.reservation?.site_pass_info_checked || false,
            };
        case GET_ORDER_DETAILS_ERROR:
            return { ...state, isLoading: false };
        case GET_MY_PASSES_REQUEST:
            return { ...state, isLoading: true };
        case GET_MY_PASSES_SUCCESS:
            return {
                ...state,
                my_passes: action.payload,
            };
        case GET_MY_PASSES_ERROR:
            return { ...state, isLoading: false };
        case GET_MY_RESERVATIONS_REQUEST:
            return { ...state, isLoading: true };
        case GET_MY_RESERVATIONS_SUCCESS:
            return {
                ...state,
                my_reservations: action.payload,
            };
        case GET_MY_RESERVATIONS_ERROR:
            return { ...state, isLoading: false };
        case OPEN_PASS_MODAL:
            return { ...state, isPassModalOpen: true };
        case CLOSE_PASS_MODAL:
            return { ...state, isPassModalOpen: false };
        case REMOVE_RESERVATION_FROM_CART_SUCCESS:
            return orderDetailsInitialState;
        case OPEN_MODIFY_MODAL:
            return { ...state, isModifyModalOpen: true };
        case CLOSE_MODIFY_MODAL:
            return { ...state, isModifyModalOpen: false };
        case TOGGLE_NEED_TO_KNOW_CHECKBOX: {
            let isNeedToKnowChecked;

            if (!isNil(action.payload)) {
                isNeedToKnowChecked = action.payload;
            } else {
                isNeedToKnowChecked = !state.isNeedToKnowChecked;
            }
            return {
                ...state,
                isNeedToKnowChecked,
            };
        }
        case TOGGLE_SITE_PASS_INFO_CHECKBOX: {
            const newState = {
                isSitePassInfoChecked: !state.isSitePassInfoChecked,
            };

            if (!isNil(action.payload)) {
                newState.isSitePassInfoChecked = action.payload;
            }

            if (!newState.isSitePassInfoChecked) {
                const reservations = [];
                newState.sitePass = {
                    ...state?.sitePass,
                    reservations,
                };
            }

            return {
                ...state,
                ...newState,
            };
        }
        case GET_DRAFT_PRICE_SUMMARY_REQUEST: {
            return { ...state, price_summary: { ...state.price_summary, loading: true } };
        }
        case GET_DRAFT_PRICE_SUMMARY_SUCCESS: {
            return { ...state, price_summary: { ...action.payload, loading: false } };
        }
        case GET_DRAFT_PRICE_SUMMARY_ERROR: {
            return {
                ...state,
                price_summary: { ...state.price_summary, loading: false },
            };
        }
        case GET_UPDATED_ORDER_SUMMARY_REQUEST: {
            return { ...state, price_summary: { ...state.price_summary, loading: true } };
        }
        case GET_UPDATED_ORDER_SUMMARY_SUCCESS: {
            return { ...state, price_summary: { ...action.payload, loading: false } };
        }
        case GET_UPDATED_ORDER_SUMMARY_ERROR: {
            return {
                ...state,
                price_summary: { ...state.price_summary, loading: false },
            };
        }
        case UPDATE_SITE_PASS_RESERVATIONS: {
            return {
                ...state,
                sitePass: {
                    ...state?.sitePass,
                    reservations: [...action.payload],
                },
            };
        }
        case UPDATE_SITE_PASS_REMOVED_RESERVATIONS_IDS: {
            return {
                ...state,
                sitePass: {
                    ...state?.sitePass,
                    removedReservationIds: [...action.payload],
                },
            };
        }
        case UPDATE_SITE_PASS_TOTAL_PRICE: {
            return {
                ...state,
                sitePass: {
                    ...state?.sitePass,
                    totalPrice: action.payload,
                },
            };
        }
        case RESET_SITE_PASS: {
            const reservations = [
                ...(orderDetailsInitialState?.sitePass?.reservations ?? []),
            ];
            const removedReservationIds = [
                ...(orderDetailsInitialState?.sitePass?.removedReservationIds ?? []),
            ];
            const totalPrice = orderDetailsInitialState?.sitePass?.totalPrice ?? 0;

            return {
                ...state,
                sitePass: {
                    reservations,
                    removedReservationIds,
                    totalPrice,
                },
            };
        }
        case GET_QR_CODES_SUCCESS: {
            return {
                ...state,
                qrCodes: action.payload,
            };
        }
        case GET_QR_CODES_ERROR: {
            return {
                ...state,
                qrCodes: {},
            };
        }
        default:
            return state;
    }
};
